import type {
  SubmitFormRequest,
  SubmitFormResponse,
  SyncResult,
} from "../offline/types";

const SUBMIT_ENDPOINT = "/api/form-submissions";

/**
 * Optional, opt-in override of where (and with what extra payload) a submission
 * POSTs.
 *
 * The public/field path submits to `/api/form-submissions` with no auth — that
 * stays the default and is used whenever no override is registered. The widget
 * embed view (`/embed/[formUrl]`) instead POSTs to the equally-public
 * `/api/widget/submit`, adding its publishable key and a freshly-minted
 * Cloudflare Turnstile token to the body.
 *
 * Why a module-level singleton rather than a per-call argument: the actual POST
 * runs inside the GLOBAL offline sync loop (`runSyncLoop` → `processOne` →
 * `submitFormToServer`), which reads queued rows back out of IndexedDB long
 * after the originating component's `submit()` returned — and is also fired by
 * connectivity/visibility/interval events that carry no call context. A closure
 * (`getExtraBody`) can't be serialized into the queue, so the mounted widget
 * form registers it here and `submitFormToServer` resolves the extra fields at
 * send time — which is what keeps the single-use Turnstile token fresh.
 */
export type SubmitEndpointOverride = {
  url: string;
  getExtraBody?: () => Promise<Record<string, unknown>>;
  /**
   * When set, the override applies ONLY to queued rows whose `formUrl` matches.
   * The offline queue is a single per-origin IndexedDB store, and the `/embed`
   * iframe is served from the app origin — so its sync loop can, in principle,
   * drain a row enqueued by a `/submit` tab in the same browser. Scoping by
   * `formUrl` guarantees the widget's token/endpoint is only ever applied to the
   * widget form's own submissions (and vice-versa a plain form never gets it).
   */
  formUrl?: string;
};

let endpointOverride: SubmitEndpointOverride | null = null;

/**
 * Register (or clear, with `null`) the submit-endpoint override. Called from
 * `FormSubmitComponent` on mount/unmount when its `submitEndpoint` prop is set.
 * With no override registered, submission behaviour is byte-identical to before.
 */
export function setSubmitEndpointOverride(
  override: SubmitEndpointOverride | null,
): void {
  endpointOverride = override;
}

// A submission POST that never settles leaves the queue row in "syncing" and —
// because the sync loop is guarded by a single in-progress flag — wedges every
// future drain attempt. Bound it: a timed-out request rejects, gets classified
// retryable below, and the loop moves on. The payload is JSON (photos upload
// separately in attachments.ts), so 30s is generous even on 2G.
const SUBMIT_TIMEOUT_MS = 30_000;

export function requestTimeoutSignal(ms: number): AbortSignal | undefined {
  return typeof AbortSignal !== "undefined" && "timeout" in AbortSignal
    ? AbortSignal.timeout(ms)
    : undefined;
}

export async function submitFormToServer(
  req: SubmitFormRequest,
): Promise<SyncResult> {
  // Only honour the override for the form it was registered for (see the
  // `formUrl` note on SubmitEndpointOverride). A formUrl-less override still
  // applies globally, preserving the original opt-in behaviour.
  const registered = endpointOverride;
  const override =
    registered && (!registered.formUrl || registered.formUrl === req.formUrl)
      ? registered
      : null;
  const url = override?.url ?? SUBMIT_ENDPOINT;

  const headers: Record<string, string> = { "Content-Type": "application/json" };

  let extraBody: Record<string, unknown> = {};
  if (override?.getExtraBody) {
    try {
      extraBody = await override.getExtraBody();
    } catch (err) {
      // Usually a Turnstile challenge that failed, timed out, or ran while the
      // device was offline. Retryable so the sync loop tries again rather than
      // permanently failing the row.
      const message = err instanceof Error ? err.message : String(err);
      return {
        ok: false,
        retryable: true,
        error: `Verification unavailable: ${message}`,
      };
    }
  }

  try {
    const res = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify({ ...req, ...extraBody }),
      signal: requestTimeoutSignal(SUBMIT_TIMEOUT_MS),
    });

    if (res.ok) {
      const data = (await res.json()) as SubmitFormResponse;
      return { ok: true, serverSubmissionId: data.serverSubmissionId };
    }

    const errorBody = await res.text().catch(() => "");
    return classifySyncError(res.status, errorBody);
  } catch (err) {
    return classifyNetworkError(err);
  }
}

function classifySyncError(status: number, body: string): SyncResult {
  if (status >= 500 || status === 429) {
    return { ok: false, retryable: true, error: `Server error ${status}: ${body}` };
  }

  // 4xx — client problem, don't retry
  return { ok: false, retryable: false, error: `Request rejected ${status}: ${body}` };
}

function classifyNetworkError(err: unknown): SyncResult {
  const message =
    err instanceof Error ? err.message : String(err);

  // AbortSignal.timeout rejects with a TimeoutError/AbortError DOMException
  // whose message ("signal timed out") doesn't match the substring patterns
  // below — match on the name so timeouts are always retryable.
  const name = err instanceof Error ? err.name : "";
  if (name === "TimeoutError" || name === "AbortError") {
    return { ok: false, retryable: true, error: message };
  }

  const networkPatterns = [
    "failed to fetch",
    "network",
    "timeout",
    "timed out",
    "aborted",
    "err_internet_disconnected",
    "net::err",
    "load failed",
  ];

  const isNetwork = networkPatterns.some((p) =>
    message.toLowerCase().includes(p),
  );

  return {
    ok: false,
    retryable: isNetwork,
    error: message,
  };
}
